import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { CONSTANTS } from '../constants/constants';
import { BehaviorSubject, iif, map, Observable } from 'rxjs';
import { EventCreateRequest } from '../types/event-request.type';
import {
  EventResponse,
  EventResponseEvent,
  EventsResponse,
  EventStatus,
} from '../types/event-response.type';
import { SearchQueryParams } from '../types/ui.type';

@Injectable({
  providedIn: 'root',
})
export class EventsHttpService {
  private url = `${CONSTANTS.BASE_API}events`;
  private _likeChanges = new BehaviorSubject<any>(null);
  public likeChanges$ = this._likeChanges.asObservable();

  constructor(private readonly http: HttpClient) {}

  getEvent(id: number): Observable<EventResponseEvent> {
    return this.http.get<EventResponseEvent>(`${this.url}/${id}`);
  }

  getEvents({
    search,
    categories,
    locations,
    dateFrom,
    dateTo,
    organization_id,
    userId,
  }: SearchQueryParams = {}): Observable<EventResponseEvent[]> {
    let params: HttpParams = new HttpParams();
    const categoryList: number[] = categories
      ? Array.isArray(categories)
        ? categories.map(Number)
        : [Number(categories)]
      : [];
    const locationList: number[] = locations
      ? Array.isArray(locations)
        ? locations.map(Number)
        : [Number(locations)]
      : [];

    if (!categoryList.length && search) {
      params = params.append('event_search', search);
    }

    if (categoryList.length) {
      categoryList.forEach((category) => {
        params = params.append('categories[]', category);
      });
    }

    if (locationList.length) {
      locationList.forEach((country) => {
        params = params.append('countries[]', country);
      });
    }

    if (dateTo) {
      params = params.append('date_to', `${dateTo}T23:59:00`);
    }

    if (dateFrom) {
      params = params.append('date_from', `${dateFrom}T00:00:00`);
    }

    if (organization_id) {
      params = params.append('organization_id', organization_id);
    }

    if (userId) {
      params = params.append('user_id', userId);
    }

    return this.http
      .get<EventsResponse>(this.url, { params })
      .pipe(map((res) => res?.data ?? []));
  }

  getPopularEvents(): Observable<EventResponseEvent[]> {
    return this.getEvents().pipe(
      map((data: EventResponseEvent[]): EventResponseEvent[] =>
        data
          .sort((a: EventResponseEvent, b: EventResponseEvent) => {
            // @ts-ignore
            return b.event_top - a.event_top;
          })
          .slice(0, 12)
      )
    );
  }

  saveUpdateEvent(
    body: EventCreateRequest,
    eventOrganizationId: number,
    eventId: number
  ): Observable<EventResponse> {
    return iif(
      () => !!eventId,
      this.updateEventV2(body, eventId),
      this.saveEventV2(body, eventOrganizationId)
    );
  }

  saveEventV2(
    body: EventCreateRequest,
    eventOrganizationId: number
  ): Observable<EventResponse> {
    return this.http.post<EventResponse>(
      `${CONSTANTS.BASE_API}organizations/${eventOrganizationId}/events`,
      body
    );
  }

  updateEventV2(
    body: EventCreateRequest,
    id: number
  ): Observable<EventResponse> {
    return this.http.patch<EventResponse>(`${this.url}/${id}`, body);
  }

  deleteEvent(id: number): Observable<Object> {
    return this.http.delete(`${this.url}/${id}`);
  }

  toggleEventLike(id: number, value: boolean): Observable<Object> {
    this._likeChanges.next({
      event_id: id,
      value,
    });
    return this.http.get(`${this.url}/${id}/like`);
  }

  updateLogo(file: File, id: number): Observable<Object> {
    const formData = new FormData();

    formData.append('file_type', 'document');
    formData.append('file_type_id', '1'); // TODO: unify type ids
    formData.append('file', file);
    formData.append('file_name', 'logo');

    return this.http.post(
      `${CONSTANTS.BASE_API}events/${id}/files/upload`,
      formData
    );
  }

  getStatuses(): Observable<{ statuses: EventStatus[] }> {
    return this.http.get<{ statuses: EventStatus[] }>(`${this.url}/statuses`);
  }
}
